import * as React from 'react';
import * as _ from 'lodash';
import { useState, useEffect, useCallback, useRef, createRef, useMemo } from 'react';
import { ITransform, ITransformRow } from '../../../../../models/ITransform';
import { IScript } from '../../../../../models/IScript';
import { Environment } from '../../../../../const/environment';
import { List, AutoSizer } from 'react-virtualized/dist/commonjs';
import ComboBox from '../../../../components/ComboBox/ComboBox';
import ConfirmPopup from '../../../../components/ConfirmPopup/ConfirmPopup';
import { TransformRow } from '../TransformRow';
import cancelEvent from '../../../../../services/mini/cancelEvent';
import { confirmAlert } from '../../../../../services/mini/alanConfirmAlert';
import { TransformTopInstructionPopup } from '../TransformTopInstructionPopup/TransformTopInstructionPopup';
import environmentNames from '../../../../../const/environmentNames';
import ReactTooltip from 'react-tooltip';
import isLocalStorageAvailable from '../../../../../services/mini/isLocalStorageAvailable';
import TextareaAutosize from 'react-textarea-autosize';
import ToasterService from '../../../../../services/ToasterService';
import addTabSpacesTotextarea from '../../../../../services/mini/addTabSpacesTotextarea';
import { HIGLIGHT_TOP_INSTRUCTION_RULES } from '../transformTextHighlight';
import projectHelper from '../../../../../services/ProjectHelper';
import { IProject } from '../../../../../models/IProject';
import { useIsMounted } from '../../../../../hooks/useIsMounted';
import { TransformFilterHeaderWrapper } from '../TransformFilterHeaderWrapper/TransformFilterHeaderWrapper';
import SaveScriptsButton from '../../SaveScriptsButton/SaveScriptsButton';
import { observer } from 'mobx-react';
import { transformsStore } from '../../../../../mobx/transforms.store';
import { defaultTransformColumnFormat, getTransformFormatsByCellType } from '../transformFormats';
import { TransformFormatListItem } from '../TransformFormatListItem/TransformFormatListItem';
import TransformConfig from '../TransformConfig/TransformConfig';
import getUrlInfo from '../../../../../services/mini/getUrlInfo';
import history from '../../../../../history';
import { routeInfo } from '../../../../../routeInfo';
import { Button, ButtonTheme } from '../../../../components/Button/Button';
import { HistoryIconSvg } from '../TransformIcons';
import TransformFilters from '../TransformFilters/TransformFilters';

const LS_COPY_KEY = 'copied-transform-row';

const ROW_HEIGHT = 150;

type TransformViewProps = {
    project: IProject,
    environment: Environment,
    previewMode: boolean,
    transformLabel: string,
    tableLabel?: string,
    script: IScript,
    transform: ITransform
    savedTransform?: ITransform
    onCopy?: (row: ITransformRow) => void,
    scrollToLastRow?: boolean,
    onScrollCb?: (inTheEnd: boolean) => void,
    withFilter?: boolean,
    linesFilter?: number[],
    isScriptsPanelCollapsed?: boolean,
    onClose?: () => void,
    onHistory?: () => void,
};

function TransformView({
    project,
    transform,
    savedTransform,
    environment,
    script,
    previewMode,
    onCopy,
    transformLabel,
    tableLabel,
    scrollToLastRow,
    onScrollCb,
    onClose,
    onHistory,
    withFilter,
    linesFilter = [],
    isScriptsPanelCollapsed

}: TransformViewProps) {

    const [updateTs, setUpdateTs] = useState(0);
    const [withScroll, setWithScroll] = useState(false);

    const rowIsAdded = useRef(false);
    const containerRef = useRef();
    const scrollRef = useRef(false);
    const listRef = useRef<List>();
    const textareaRef = useRef<any>();
    const highlightPluginRef = useRef({ wasApplied: false });
    const isMounted = useIsMounted();

    const rememberScrollPos = useCallback((y) => {
        if (isLocalStorageAvailable()) {
            localStorage.setItem(`transform-table-scroll-${script.scriptId}`, y);
        }
    }, [script.scriptId]);

    const handleTableScroll = useCallback(({ clientHeight, scrollHeight, scrollTop }) => {
        rememberScrollPos(scrollTop);
        if (clientHeight > 0) {
            if (
                (scrollHeight - scrollTop === clientHeight) ||
                (scrollTop === 0 && clientHeight > scrollHeight)
            ) { // we at the end of the list
                if (onScrollCb) {
                    onScrollCb(true);
                }
            } else {
                if (onScrollCb) {
                    onScrollCb(false);
                }
            }
        }

    }, [onScrollCb, rememberScrollPos]);

    const scrollTableTolastRow = useCallback((scrollTop?) => {
        if (listRef.current && !previewMode) {
            listRef.current.scrollToRow(transform.rows?.length - 1);
            transformsStore.resetRowToCopy();
        }
    }, [previewMode, transform.rows.length]);


    useEffect(() => {
        if (isLocalStorageAvailable() && !scrollToLastRow) {
            const scrollTop = localStorage.getItem(`transform-table-scroll-${script.scriptId}`);
            scrollTableTolastRow(scrollTop);
        }

    }, [script.scriptId, scrollTableTolastRow, scrollToLastRow]);


    useEffect(() => {
        if (rowIsAdded.current === true) {
            scrollTableTolastRow();
            rowIsAdded.current = false;
        }
    }, [rowIsAdded, scrollTableTolastRow]);

    useEffect(() => {
        if (scrollToLastRow) {
            scrollTableTolastRow();
        }
    }, [scrollTableTolastRow, scrollToLastRow]);


    const addRow = () => {
        transformsStore.addRow();
        rowIsAdded.current = true;
    };

    const duplicateRow = useCallback((row: ITransformRow) => {
        transformsStore.duplicateRow(row);
        rowIsAdded.current = true;

        if (listRef.current) {
            listRef.current.scrollToRow(transform.rows?.length + 1);
        }

    }, [listRef, transform.rows.length]);

    const updateFormat = useCallback((prop, value) => {
        transformsStore.updateFormat(prop, value);
    }, []);

    const updatePromptValue = useCallback((value) => {
        transformsStore.updateMainPrompt(value);
    }, []);


    const commonPromptLabel = 'Instruction';
    const commonPromptValue = transform.header?.prompt?.value || '';

    const isNotStage = environment !== Environment.Staging;
    const isLastTag = projectHelper.isLastTag(project, project[`${environment}TagId`]);

    const filterMsg = `Rows ${linesFilter.map(el => el + 1).join()} are shown`;

    const openEditPopupCb = useCallback(() => {
        confirmAlert({
            customUI: ({ onClose }) => {
                return (
                    <TransformTopInstructionPopup
                        editable={!isNotStage && isLastTag}
                        hideFormatSelector={true}
                        title={commonPromptLabel.toLowerCase()}
                        initValue={commonPromptValue}
                        savedTransform={savedTransform}
                        onClose={onClose} />
                );
            }
        });
    }, [commonPromptValue, isLastTag, isNotStage, updatePromptValue]);

    const onFormatUpdateCb = useCallback((name, val) => {
        updateFormat(name, val);
    }, [updateFormat]);

    const applyHighlighting = useCallback(() => {
        try {
            highlightPluginRef.current.wasApplied = true;
            window.$('.textarea-with-highlighted-top-instruction-text', containerRef?.current).highlightWithinTextarea({
                highlight: HIGLIGHT_TOP_INSTRUCTION_RULES
            });
        } catch (error) {
            console.info('err', error);
        }
    }, []);

    const updateHighlighting = useCallback(() => {
        try {
            if (highlightPluginRef.current?.wasApplied) {
                window.$('.textarea-with-highlighted-top-instruction-text', containerRef?.current).highlightWithinTextarea('update');
            }
        } catch (error) {
            console.info('err', error);
        }
    }, []);

    useEffect(() => {
        if (isMounted) {
            applyHighlighting();
        }
    }, [applyHighlighting, isMounted]);

    const handleTextareaKeydown = useCallback((e) => {
        addTabSpacesTotextarea(e);
        updateHighlighting();
    }, [updateHighlighting, updatePromptValue]);

    const hasRowsInBuffer = isLocalStorageAvailable() && localStorage.getItem(LS_COPY_KEY);

    useEffect(() => {
        updateHighlighting();
    }, [updateHighlighting, transform.header.prompt.value]);

    const handleCopyRowToBuffer = useCallback((row) => {
        if (isLocalStorageAvailable()) {
            localStorage.setItem(LS_COPY_KEY, JSON.stringify(previewMode ? { ...row, prompt: '' } : row));
        }
        setUpdateTs(Date.now());
        ToasterService.showInfo({ title: !withFilter ? 'Row was copied!' : 'Row was copied! Clear filters to access editing transforms.' });
    }, [withFilter, previewMode]);

    const handleFilterIconClick = useCallback(() => {
        transformsStore.resetLineFilters();
    }, [filterMsg]);

    const handleSearchChange = useCallback((prop, value) => {
        transformsStore.search(prop, value, previewMode);
    }, [previewMode]);

    const goToTransformCb = useCallback(() => {
        if (onClose) {
            onClose();
        }
        history.push(routeInfo.projectScripts.url(project.name, script.name))
    }, [project, script]);


    let rowsToDisplay = transform?.rows?.filter(row => row.visible);



    if (linesFilter?.length > 0) {
        const orderedRowsMap = new Map();
        rowsToDisplay = rowsToDisplay.filter(row => row.visible);
        linesFilter.forEach((id, index) => orderedRowsMap.set(id, index));
        rowsToDisplay = rowsToDisplay.sort((a, b) => {
            return orderedRowsMap.get(a.rowIndex) - orderedRowsMap.get(b.rowIndex);
        });
    }

    const readonly = isNotStage || !isLastTag;

    // console.info('transform.rows', toJS(rowsToDisplay.map(r => toJS(r))));
    const setWithScrollDebounced = useCallback(_.debounce((scroll) => setWithScroll(scroll), 100), []);


    const { scriptName: currentScriptName } = getUrlInfo();

    const withTransformLink = currentScriptName !== script.name;

    return <div className={`transform-view ${(previewMode) && 'preview-mode'} ${!previewMode && isNotStage ? 'limited-mode' : ''}`}>


        <div className="transform-view__header">
            <div className="transform-view__header-label">
                {(isNotStage && !previewMode) &&
                    <><ReactTooltip id="transformNotStageTooltip" />
                        <i
                            data-for="transformNotStageTooltip"
                            data-tip={`You can only view transforms in the ${environmentNames[environment]} environment. To edit transforms, switch to the Staging environment.`}
                            data-place="bottom"
                            data-effect="solid"
                            data-class="tutor-light-tooltip tutor-no-arrow-tooltip custom-logo-is-not-available-tooltip"
                            className="icon icon-alan-info-sign transform-view__not-satge-tooltip"></i></>
                }
                {transformLabel}: <b
                    className={withTransformLink ? 'transform-view__script-url' : ''}
                    onClick={withTransformLink ? goToTransformCb : undefined}
                >{script?.name || '-'}</b>
            </div>
            {previewMode !== true && <div className="transform-view__header-controls">
                <TransformConfig
                    transform={transform}
                    savedTransform={savedTransform}
                    touched={transformsStore.configTouched}
                    disabled={readonly}
                />
                <Button
                    className='transform-view__history-btn'
                    label="History"
                    theme={ButtonTheme.LightBlueWithBorder}
                    onClick={onHistory ? onHistory : undefined}
                    icon={<HistoryIconSvg />}
                />
            </div>}
        </div>
        {!previewMode && <div className="transform-view__command-prompt" >
            <label className="transform-view__label">{commonPromptLabel}</label>
            <div className={`transform-view__command-prompt-wrapper ${commonPromptValue !== savedTransform?.header?.prompt?.value ? 'has-changes' : ''}`} ref={containerRef}>
                <TextareaAutosize
                    ref={textareaRef}
                    className={`transform-view__command-prompt-input textarea-with-highlighted-top-instruction-text `}
                    minRows={2}
                    maxRows={15}
                    value={commonPromptValue}
                    readOnly={readonly}
                    onChange={(e) => {
                        updatePromptValue(e.target.value);
                    }}
                    onKeyDown={handleTextareaKeydown}
                />
            </div>

            <div className="transform-view__popup-btn" onClick={(e) => {
                openEditPopupCb();
                cancelEvent(e);
            }}>
                <i className="icon icon-alan-search" />
            </div>
        </div>}

        {!previewMode && <div className="transform-view__middle-panel">
            {tableLabel && <label className="transform-view__label transform-view__tabel-label">{tableLabel}</label>}
            <TransformFilters
                filterMsg={filterMsg}
                linesFilter={linesFilter} />
        </div>}

        <div className={`transform-view__table`} >
            <div className={`transform-view__table-row header ${withScroll && 'with-scroll'}`}>

                <div>
                    <div className="transform-view__table-row-header-content">
                        <div> #
                            {withFilter && <i
                                title={filterMsg ? filterMsg : ''}
                                onClick={handleFilterIconClick}
                                className="icon icon-alan-filter transform-view__table-filter-icon" />}
                        </div>
                    </div>
                </div>
                <div className={`transform-view__table-cell-header`}>
                    <div className={`transform-view__table-row-header-content ${!previewMode && transform.header?.input?.format !== savedTransform?.header?.input?.format ? 'has-changes' : ''}`}>
                        <TransformFilterHeaderWrapper
                            name="input"
                            label="Input"
                            onChange={handleSearchChange}>
                            {!previewMode && <ComboBox
                                classes="transform-view__cb"
                                values={getTransformFormatsByCellType(transform.header?.input?.format || defaultTransformColumnFormat, 'input')}
                                valueKey="value"
                                valueName="label"
                                disabled={readonly}
                                listItemCount={5}
                                selectedValueId={transform.header?.input?.format || defaultTransformColumnFormat}
                                renderItem={(item) => <TransformFormatListItem
                                    item={item}
                                    className='transform-view__cb-item'
                                />}
                                onValueSelect={(formatItem) => {
                                    updateFormat('input', formatItem.value);
                                }}
                            />}
                        </TransformFilterHeaderWrapper>

                    </div>

                </div>
                <div className={`transform-view__table-cell-header`}>
                    <div className={`transform-view__table-row-header-content ${!previewMode && transform.header?.query?.format !== savedTransform?.header?.query?.format ? 'has-changes' : ''}`}>
                        <TransformFilterHeaderWrapper
                            name="query"
                            label="Query"
                            onChange={handleSearchChange}>
                            {!previewMode && <ComboBox
                                classes="transform-view__cb"
                                values={getTransformFormatsByCellType(transform.header?.input?.format || defaultTransformColumnFormat, 'query')}
                                valueKey="value"
                                valueName="label"
                                disabled={readonly}
                                listItemCount={5}
                                selectedValueId={transform.header?.query?.format || defaultTransformColumnFormat}
                                renderItem={(item) => <TransformFormatListItem
                                    item={item}
                                    className='transform-view__cb-item'
                                />}
                                onValueSelect={(formatItem) => {
                                    updateFormat('query', formatItem.value);
                                }}
                            />}
                        </TransformFilterHeaderWrapper>
                    </div>
                </div>
                {!previewMode &&
                    <div>
                        <div className="transform-view__table-row-header-content">
                            <TransformFilterHeaderWrapper
                                name="prompt"
                                label='Instruction'
                                onChange={handleSearchChange}>
                            </TransformFilterHeaderWrapper>
                        </div>
                    </div>}
                <div className={`transform-view__table-cell-header`}>
                    <div className={`transform-view__table-row-header-content ${!previewMode && transform.header?.result?.format !== savedTransform?.header?.result?.format ? 'has-changes' : ''}`}>
                        <TransformFilterHeaderWrapper
                            name="result"
                            label='Result'
                            onChange={handleSearchChange}>
                            {!previewMode && <ComboBox
                                classes="transform-view__cb"
                                values={getTransformFormatsByCellType(transform.header?.input?.format || defaultTransformColumnFormat, 'result')}
                                valueKey="value"
                                valueName="label"
                                disabled={readonly}
                                listItemCount={5}
                                selectedValueId={transform.header?.result?.format || defaultTransformColumnFormat}
                                renderItem={(item) => <TransformFormatListItem
                                    item={item}
                                    className='transform-view__cb-item'
                                />}
                                onValueSelect={(formatItem) => {
                                    updateFormat('result', formatItem.value);
                                }}
                            />}
                        </TransformFilterHeaderWrapper>
                    </div>
                </div>
                <div className="transform-view__table-row-header-content">
                    {previewMode ? <span className="transform-view__table-row-preview-header-command-content">Add to transform example</span> : ''}
                </div>
            </div>
            <div className="transform-view__table-body">
                {rowsToDisplay?.length > 0 ?
                    <AutoSizer>
                        {({ height, width }) => {
                            return <List
                                ref={listRef}
                                width={width}
                                height={height}
                                rowCount={rowsToDisplay?.length}
                                rowHeight={ROW_HEIGHT}
                                onScroll={handleTableScroll}
                                overscanRowCount={1}
                                rowRenderer={(p) => {
                                    const { index, key, style, parent } = p;
                                    const rowForList = rowsToDisplay[index];
                                    const savedRowForList = _.find(savedTransform?.rows, { rowIndex: rowForList.rowIndex });

                                    if (index === 0) {
                                        scrollRef.current = parent.props.height < rowsToDisplay?.length * ROW_HEIGHT;
                                        if (scrollRef.current !== withScroll) {
                                            setWithScrollDebounced((scrollRef.current));
                                        }
                                    }

                                    return <div key={key} style={style}>
                                        <TransformRow
                                            transformName={script?.name || ''}
                                            previewMode={previewMode}
                                            environment={environment}
                                            readOnly={readonly}
                                            index={rowForList?.rowIndex !== undefined ? rowForList?.rowIndex : index}
                                            row={rowForList}
                                            savedRow={savedRowForList}
                                            projectId={project.projectId}
                                            header={transform.header}
                                            isHighlighted={scrollToLastRow && index === transform.rows.length - 1}
                                            onFormatUpdate={onFormatUpdateCb}
                                            onDuplicate={duplicateRow}
                                            onCopyToBuffer={handleCopyRowToBuffer}
                                            onCopy={onCopy}
                                        />
                                    </div>;
                                }}
                            />;
                        }}
                    </AutoSizer> :
                    <div className="transform-view__nothing-found">
                        Nothing was found
                    </div>}
            </div>
        </div>

        {!previewMode &&
            <div className="transform-view__bottom-controls">
                <div className="transform-view__bottom-left-controls">
                    {isScriptsPanelCollapsed &&
                        <SaveScriptsButton
                            key={updateTs}
                            environment={environment}
                            className="transform-view__save-all-btn"
                            mode="normal" />}

                    <div
                        className={`transform-view__add-row-btn action-btn ${withFilter ? 'disabled' : ''}`}
                        onClick={addRow}>
                        <i className="icon icon-alan-plus-stroked"></i>
                        Add Row</div>
                    {hasRowsInBuffer &&
                        <div className={`transform-view__paste-row-btn action-btn ${withFilter ? 'disabled' : ''}`}
                            onClick={() => {
                                let rowFromBuffer = null;
                                try {
                                    rowFromBuffer = JSON.parse(localStorage.getItem(LS_COPY_KEY));
                                    duplicateRow(rowFromBuffer);
                                } catch (error) {

                                }
                            }}>
                            <i className="icon icon-alan-stat-sort-up"></i>
                            Paste Copied Row</div>}
                    {hasRowsInBuffer &&
                        <div
                            className={`transform-view__clear-copied-row-btn action-btn light-red-with-border ${withFilter ? 'disabled' : ''}`}
                            onClick={() => {
                                try {

                                    confirmAlert({
                                        customUI: ({ onClose }) => {
                                            return (
                                                <ConfirmPopup
                                                    onClose={onClose}
                                                    okBtnLabel="Clear"
                                                    okBtnCb={() => {
                                                        localStorage.removeItem(LS_COPY_KEY);
                                                        setUpdateTs(Date.now());
                                                        onClose();
                                                    }}
                                                    title="Clear copy buffer"
                                                    message={<div>Are you sure you want to clear the copied row from the buffer?</div>}
                                                />
                                            );
                                        }
                                    });


                                } catch (error) {

                                }
                            }}>
                            <i className="icon icon-alan-test-error"></i>
                            Clear Copy Buffer</div>}
                </div>
            </div>
        }
    </div >;
}


export default observer(TransformView);


